import { globals } from 'config/globals';
import { Core } from 'core/Core';
import { device } from 'utils/device';
import { Size } from './WorkTilePixi';
import { MouseController } from 'app/chrome/mouse/MouseController';

import { Inertia } from 'utils/motion/Inertia';
import ProjectHeaderSizes from 'app/views/project/ProjectHeaderSizes';
import Config from 'config/settings';
import SiteTransitions from 'app/services/SiteTransitions';

export interface WorkTileParticleConfig {
	name?: string;
	midAlpha?: number;
	alpha?: ParticleAnimationConfig;

	midScale?: number;
	scale?: ParticleAnimationConfig;
	standardScale?: number;
	mobileScale?: number;

	x?: ParticleAnimationConfig;
	standardX?: number;
	standardXOrg?: number;
	mobileX?: number;
	mobileXOrg?: number;
	y?: ParticleAnimationConfig;
	standardY?: number;
	standardYOrg?: number;
	mobileY?: number;
	mobileYOrg?: number;
	projectScale?: number;

	projectX?: number;
	projectY?: number;
	toProjectTimeOffset?: number;
	mobileProjectX?: number;
	mobileProjectY?: number;
	projectMobileScale?: number;
	projectAnimationOffset?: number;

	rotation?: ParticleAnimationConfig;
	//Passed in settings
	texture: PIXI.Texture;
	xOrg?: number;
	yOrg?: number;
	speed?: number;
	autoRotate?: boolean;
	autoScale?: boolean;
	autoAlpha?: boolean;

	//startY endY //Positions of end relative to image anchor center -1 is aligned left +1 is aligned right:
	leftY?: number;
	rightY?: number;
	retainInCaseStudy?: boolean; //Hid in basic way
	caseStudyHide?: boolean; //Hide using project settings
	parallaxPower?: number;
	caseStudyScrollParallax?: number;
	caseStudyScrollRatio?: number;
}

interface ParticleAnimationConfig {
	value: number;
	start: number;
	end: number;
	startRange?: number;
	endRange?: number;
	org?: number;
}

export class WorkTileParticlePixi {
	public debug: boolean = false;

	private _$image: PIXI.Sprite;
	private _$backoutImage: PIXI.Sprite;
	//Write-only (assigned in setSavedScale flow but never read — public so
	//noUnusedLocals doesn't flag it; candidate for removal in a runtime pass):
	public _savedScale: number;
	private _scale: number;
	private _x: number;
	private _y: number;
	private _xCaseStudy: number;
	private _yCaseStudy: number;
	private _parallaxSpeed: number = 0;
	private _parallaxEasePower: number = 10;

	private _displayScale: number = 1;

	private _config: WorkTileParticleConfig;

	private _mouse: MouseController;

	private _xParallax: Inertia;
	private _yParallax: Inertia;
	private _caseStudyScrollParallaxY: number = 0;
	private _caseStudyScrollParallaxTarget: number = 0;
	private _projectX: number;
	private _projectY: number;
	private _projectScale: number = 1;
	private _parallaxPower: number = 1;
	private _parallaxSpeedDampener: number;
	private _depth: number;
	private _moveMuliplier: number = 1;
	private _parallaxActiveMultipler: number = 0;
	private _midAlpha: number = 1;

	private _onCaseStudy: boolean = false;

	private _workIndex: number;
	private _particleIndex: number;

	private _projectExitScrolling: boolean = false;

	constructor(
		_$pixi: PIXI.Application,
		private _$container: PIXI.Container,
		config: WorkTileParticleConfig,
		_isMasked: boolean,
		depth: number,
		workIndex: number,
		particleIndex: number
	) {
		this._workIndex = workIndex;
		this._particleIndex = particleIndex;

		this._config = config;


		this._config.projectAnimationOffset = config.projectAnimationOffset || 0;

		var s = config.midScale || 1;
		this._config.scale = {
			value: s,
			start: !config.autoScale ? s : 0,
			end: !config.autoScale ? s : 0
		};

		this._config.autoScale = config.autoScale;

		this._config.retainInCaseStudy = config.retainInCaseStudy;
		this._config.scale.startRange = this._config.scale.value - this._config.scale.start;
		this._config.scale.endRange = this._config.scale.value - this._config.scale.end;

		var a = config.midAlpha || 1;
		this._midAlpha = a;
		this._config.alpha = {
			value: a,
			start: !config.autoAlpha ? a : 0,
			end: !config.autoAlpha ? a : 0
		};

		this._config.alpha.startRange = this._config.alpha.value - this._config.alpha.start;
		this._config.alpha.endRange = this._config.alpha.value - this._config.alpha.end;
		50 + Math.random() * 1000;

		//X/Y values need to be calculated releative to image:
		//TODO has releance on resize running to work - so fix.
		this._config.x = { value: 0, start: 0, end: 0, org: config.xOrg };

		let startY = config.leftY ? config.leftY : config.yOrg;
		let endY = config.rightY ? config.rightY : config.yOrg;
		this._config.y = { value: 0, start: startY, end: endY, org: config.yOrg };

		var r = 0;
		this._config.rotation = {
			value: r,
			start: !config.autoRotate ? 0 : r + (Math.random() * 60 * Math.PI) / 180,
			end: !config.autoRotate ? 0 : r - (Math.random() * 60 * Math.PI) / 180
		};

		this._config.rotation.startRange = this._config.rotation.value - this._config.rotation.start;
		this._config.rotation.endRange = this._config.rotation.value - this._config.rotation.end;

		//Set the parallax speed if passed. Determinds how much the partcile will move on the x (to create a sense of parallax)
		if (config.speed) {
			this._parallaxSpeed = config.speed;
		}

		this._parallaxPower = config.parallaxPower ? config.parallaxPower : 1;

		this._createParticle(depth);
		this._mouse = Core.controller.get('Mouse') as MouseController;

		this._xParallax = new Inertia(1, 0);
		this._yParallax = new Inertia(1, 0);

		this._depth = depth;
	}

	public setProjectActive(active: boolean) {

		let ob = { value: this._parallaxActiveMultipler };
		let target = active ? 1 : 0;

		gsap.TweenMax.to(ob, 1, {
			ease: Config.linearEase,
			value: target,
			onUpdate: () => {
				this._parallaxActiveMultipler = ob.value;
			}
		});
	}

	//Update the item ro animate
	//NOTE: velocity is accepted (WorkTilePixi passes the drag velocity, and the
	//comment below says rotation was meant to be driven by it) but is NOT fed
	//into getUpdateProps — rotation comes from the static rotationConfig only.
	//It used to be passed as a 5th argument that getUpdateProps never declared,
	//so JS silently dropped it; the velocity-driven effect is likewise disabled
	//in the sibling WorkTileImagePixi (hard-zeroed there). Ask before wiring it
	//up for real.
	//Rotation of image is determined by velocity of movement and so passed in:

	public update(
		progress: number,
		parallaxRatio: number = 1.0,
		parentSize: Size,
		blackOutAlpha: number,
		velocity: number
	): void {
		let updateProps = this.processUpdate(
			this.getUpdateProps(parentSize, progress, parallaxRatio, blackOutAlpha)
		);
		this._xParallax.tick();
		this._yParallax.tick();

		this._x = updateProps.x;
		this._y = updateProps.y;
		this._scale = updateProps.scale;

		this.setUpdate(updateProps);
	}

	public updateViaProps(updateProps: any): void {
		this._xParallax.tick();
		this._yParallax.tick();

		this._x = updateProps.x;
		this._y = updateProps.y;
		this._scale = updateProps.scale;

		this.setUpdate(updateProps);
	}


	//Get the props that will passed to Parallal JS to allow calculations to be made:
	public getUpdateProps(imageSize: any, progress: number, parallaxRatio: number, blackOutAlpha: number): any {
		return {
			standardX: this._config.standardX,
			mobileX: this._config.mobileX,
			standardY: this._config.standardY,
			mobileY: this._config.mobileY,
			mobileScale: this._config.mobileScale,
			standardScale: this._config.standardScale,
			autoScale: this._config.autoScale,
			isPortraitDevice: device.isPortraitDevice,
			parallaxSpeed: this._parallaxSpeed,
			parallaxSpeedDampener: this._parallaxSpeedDampener,
			moveMuliplier: this._moveMuliplier,
			innerWidth: device.innerWidth,
			imageSize: imageSize,
			blackOutAlpha: blackOutAlpha,
			progress: progress,
			parallaxRatio: parallaxRatio,
			xConfig: this._config.x,
			yConfig: this._config.y,
			scaleConfig: this._config.scale,
			alphaConfig: this._config.alpha,
			rotationConfig: this._config.rotation,
			x: this._x,
			y: this._y,
			scale: this._scale,
			workIndex: this._workIndex,
			particleIndex: this._particleIndex
		};
	}

	//Run caculations on
	public processUpdate(props: any) {

		//Recalculate start/on/off x/y of paricles ------------------------------------------------------------------------------------------------
		//These are all set as relative to the center of the image space
		let xOrg = props.isPortraitDevice ? props.mobileX : props.standardX;
		let yOrg = props.isPortraitDevice ? props.mobileY : props.standardY;

		props.xConfig.value = props.imageSize.width / 2 + (props.imageSize.width / 2) * xOrg;
		props.yConfig.value = props.imageSize.height / 2 + (props.imageSize.height / 2) * yOrg;
		props.xConfig.start = props.xConfig.value;
		props.xConfig.endRange = props.imageSize.width * (props.parallaxSpeed * props.parallaxSpeedDampener);
		props.yConfig.endRange = 0;
		props.xConfig.startRange = props.imageSize.width * (props.parallaxSpeed * props.parallaxSpeedDampener);
		props.yConfig.startRange = 0;

		//Calculate the scale config ---------------------------------------------------------------------------------------------------------------------
		let s = props.isPortraitDevice ? props.mobileScale : props.standardScale;
		if (!s && s !== 0) {
			s = 1;
		}

		props.scaleConfig = {
			value: s,
			start: !props.autoScale ? s : 0,
			end: !props.autoScale ? s : 0
		};

		props.scaleConfig.startRange = props.scaleConfig.value - props.scaleConfig.start;
		props.scaleConfig.endRange = props.scaleConfig.value - props.scaleConfig.end;

		//Set the
		//-----------------------------------------------------------------------------------------------------------------

		let scale = 0;
		let x = 0;
		let y = 0;

		if (props.progress < 0.5) {
			props.processedProgress = props.progress / 0.5;
			scale = props.scaleConfig.start + props.scaleConfig.startRange * props.processedProgress;
			props.alpha = props.alphaConfig.start + props.alphaConfig.startRange * props.processedProgress;

			let startXRange = (props.innerWidth / 1920) * props.xConfig.startRange;
			x = props.xConfig.start - (startXRange - startXRange * props.processedProgress);
			y = props.yConfig.value + props.yConfig.startRange * (1.0 - props.processedProgress);
			props.rotation = props.rotationConfig.start + props.rotationConfig.startRange * props.processedProgress;
		} else {
			let endXRange = (props.innerWidth / 1920) * props.xConfig.endRange;

			props.progress -= 0.5;
			props.processedProgress = props.progress / 0.5;
			scale = props.scaleConfig.value - props.processedProgress * props.scaleConfig.endRange;
			props.alpha = props.alphaConfig.value - props.processedProgress * props.alphaConfig.endRange;
			x = props.xConfig.value + props.processedProgress * endXRange;
			y = props.yConfig.value - props.processedProgress * props.yConfig.endRange;
			props.rotation = props.rotationConfig.value - props.processedProgress * props.rotationConfig.endRange;
		}

		if (!props.x && props.x !== 0) props.x = x;
		if (!props.y && props.y !== 0) props.y = y;
		if (!props.scale && props.scale !== 0) props.scale = scale;

		props.x += (x - props.x) * props.moveMuliplier;
		props.y += (y - props.y) * props.moveMuliplier;
		props.scale += (scale - props.scale) * props.moveMuliplier;

		return {
			x: props.x,
			y: props.y,
			scale: props.scale,
			rotation: props.rotation,
			alpha: props.alpha,
			parallaxRatio: props.parallaxRatio,
			blackOutAlpha: props.blackOutAlpha,
			workIndex: this._workIndex,
			particleIndex: this._particleIndex
		};
	}

	//App has been sized so save the new scale of the tile (As all partciles are scale and move relative to the image)
	public resize(imageDisplayScale: number, computerContainerScale: number): void {
		//Inner particles are already scaled by the


		this._displayScale = imageDisplayScale;


		//Particle movement should be dampened on
		//Up to 500 pixels then allow full speed as set in config
		//When at 1920 then dampen this to 0.1

		this._parallaxSpeedDampener = 1.0 - Math.min(0.9, ((device.innerWidth - 500) / (1920 - 500)) * 0.9);
	}

	//Update the Pixi values based on processed props:
	public setUpdate(props: any) {
		//Set the state of the image:
		this._$image.alpha = props.alpha;

		this._savedScale = props.scale;
		this._$image.scale.set(props.scale * this._displayScale, props.scale * this._displayScale);

		this._setPosition(props.parallaxRatio);

		this._$image.rotation = props.rotation;

		if (this._$backoutImage) {
			this._$backoutImage.rotation = this._$image.rotation;
			this._$backoutImage.position = this._$image.position;
			this._$backoutImage.scale = this._$image.scale;
			this._$backoutImage.alpha = props.blackOutAlpha;
		}
	}


	public startExitScroll(maskStyle) {

		if (maskStyle) {
			this._caseStudyScrollParallaxY = 0;
			this._caseStudyScrollParallaxTarget = 0;
			this._setPosition(0, true);
		}

		this._projectExitScrolling = true;

	}

	/** Zoom to project on click */
	public zoomToProject(
		imageScale: number,
		tileEndX: number,
		tileEndY: number,
		tileScale: number,
		instantResize: boolean,
		//Optional: WorkTilePixi._zoomToProject calls with 5 args, so this is undefined
		//there (the tween map is then keyed 'undefined' — pre-existing behaviour):
		particleIndex?: number
	): void {
		if (device.isPortraitDevice) {
			this._projectX =
				this._config.mobileProjectX || this._config.mobileProjectX === 0
					? this._config.mobileProjectX
					: this._config.mobileXOrg;
			this._projectY =
				this._config.mobileProjectY || this._config.mobileProjectY === 0
					? this._config.mobileProjectY
					: this._config.mobileYOrg;
			this._projectScale =
				this._config.projectMobileScale || this._config.projectMobileScale === 0
					? this._config.projectMobileScale
					: this._config.mobileScale;
		} else {
			this._projectX =
				this._config.projectX || this._config.projectX === 0 ? this._config.projectX : this._config.standardXOrg;
			this._projectY =
				this._config.projectY || this._config.projectY === 0 ? this._config.projectY : this._config.standardYOrg;
			this._projectScale =
				this._config.projectScale || this._config.projectScale === 0
					? this._config.projectScale
					: this._config.standardScale;
		}


		this._caseStudyScrollParallaxTarget = this._caseStudyScrollParallaxY = 0;
		this._projectExitScrolling = false;

		if (this._config.retainInCaseStudy) {
			let headerSize = ProjectHeaderSizes.headerSize();

			this._caseStudyScrollParallaxY = 0;

			let alpha = this._config.caseStudyHide ? 0 : this._$image.alpha;

			if (this._depth === -3) {
				this._xCaseStudy = (1920 / 2) * (this._projectX * 2 - 1);
				this._yCaseStudy = (737 / 2) * (this._projectY * 2 - 1);

				SiteTransitions.tweens.workTileParticles[particleIndex] = gsap.TweenMax.to(
					this._$image,
					0.7 * SiteTransitions.speedScale,
					{
						pixi: {
							scaleY: 1,
							scaleX: 1,
							rotation: 0,
							ease: Config.standardEase,
							alpha: alpha,
							onComplete: () => {
								SiteTransitions.tweens.workTileParticles[particleIndex] = null;
							}
						}
					}
				);
			} else {
				this._xCaseStudy = -1 * tileEndX + this._projectX * headerSize.width;
				this._yCaseStudy = -1 * tileEndY + this._projectY * headerSize.height;

				SiteTransitions.tweens.workTileParticles[particleIndex] = gsap.TweenMax.to(
					this._$image,
					(1 + this._config.projectAnimationOffset) * Config.toProjectMultipler * SiteTransitions.speedScale,
					{
						pixi: {
							scaleY: tileScale * this._projectScale,
							scaleX: tileScale * this._projectScale,
							x: this._xCaseStudy,
							y: this._yCaseStudy,
							rotation: 0,
							alpha: alpha
						},
						ease: Config.toFloatingElementEase,
						delay: 0,
						onComplete: () => {
							this._onCaseStudy = true;
							this._easeInParallaxEffect(true);
							SiteTransitions.tweens.workTileParticles[particleIndex] = null;
						}
					}
				);

				let ran = Math.random() * 3;
				if (ran >= 1) gsap.Power2.easeOut;
				else if (ran >= 2) gsap.Power1.easeOut;

			}

			//Set parallax to new value for after tween parallax:

			this._xParallax.ease = this._yParallax.ease = 10;


			if (!instantResize) {
				this._stopAndResetParallax();
			}

		} else {
			SiteTransitions.tweens.workTileParticles[particleIndex] = gsap.TweenMax.to(
				this._$image,
				Config.toProjectTime * SiteTransitions.speedScale,
				{
					pixi: {
						scaleY: imageScale * 1,
						scaleX: imageScale * 1,
						ease: Config.toProjectEase,
						alpha: 0,
						onComplete: () => {
							SiteTransitions.tweens.workTileParticles[particleIndex] = null;
						}
					}
				}
			);
		}
	}

	/** Zoom back from project on close press */
	public zoomFromProject(displayScale, parentSize, progress, parallaxRatio, particleIndex): void {
		this._xParallax.ease = this._yParallax.ease = 100;
		this._xParallax.easeTarget = this._yParallax.easeTarget = 1;
		this._xParallax.easeFrameUpdate = this._yParallax.easeFrameUpdate = 1.6;

		this._displayScale = displayScale;
		//(4-arg call: a trailing velocity 0 used to be passed here too — see the
		//note on update() — getUpdateProps has never taken it.)
		let updateProps = this.getUpdateProps(parentSize, progress, parallaxRatio, 0);

		let scale = updateProps.scale * this._displayScale;
		let x = this._config.x.start;
		let y = this._config.y.value;

		this._y = y;
		this._x = x;

		this._onCaseStudy = false;

		SiteTransitions.tweens.workTileParticles[particleIndex] = gsap.TweenMax.to(
			this._$image,
			Config.fromFloatingElementBaseTime * Config.fromProjectMultipler * SiteTransitions.speedScale,
			{
				delay: 0,
				ease: Config.fromFloatingElementEase,
				pixi: { scaleY: scale, scaleX: scale, x: x, y: y, alpha: this._midAlpha },
				onComplete: () => {
					this._easeInParallaxEffect(false);
					SiteTransitions.tweens.workTileParticles[particleIndex] = null;
				}
			}
		);

		this._stopAndResetParallax();
	}

	private _stopAndResetParallax() {
		this._xParallax.active = false;
		this._yParallax.active = false;
		this._xParallax.value = this._xParallax.target = 0;
		this._yParallax.value = this._yParallax.target = 0;
	}

	private _easeInParallaxEffect(toProject: boolean = false) {
		this._xParallax.active = this._yParallax.active = true;
		this._xParallax.ease = 100;
		this._yParallax.ease = 100;

		if (toProject) {
			this._xParallax.easeTarget = this._yParallax.easeTarget = this._parallaxSpeed * 4;
		} else {
			this._xParallax.easeTarget = this._yParallax.easeTarget = 1;
		}

		this._xParallax.easeFrameUpdate = this._yParallax.easeFrameUpdate = 1.6;
	}

	//When case study is scrolling feed in data (as can used for parallax)
	public scroll(y: number): void {
		if (this._projectExitScrolling) {
			return;
		}

		if (this._config.retainInCaseStudy) {

			let scale = 0.5;
			let target = this._projectExitScrolling ? -1 * y * scale : -1 * this._config.caseStudyScrollRatio * y * scale;

			this._caseStudyScrollParallaxTarget = target;
			this._caseStudyScrollParallaxY = target;
		}
	}

	public exitScroll(y: number) {
		if (this._config.retainInCaseStudy) {
			let target = -1 * y;

			this._caseStudyScrollParallaxTarget = target;
			this._caseStudyScrollParallaxY = target;
			this._setPosition(0, true);
		}
	}

	public resetToZeroScroll(): void {
		this._caseStudyScrollParallaxY = this._caseStudyScrollParallaxTarget = 0;
		this._$image.y = this._yCaseStudy;
	}

	/** Called as a resulty of ProjectController.tick() . Handles parallax/sacroll on that page */
	public caseStudyTick(): void {
		if (!this._onCaseStudy || this._projectExitScrolling) {
			return;
		}

		this._caseStudyScrollParallaxY =
			this._caseStudyScrollParallaxY + (this._caseStudyScrollParallaxTarget - this._caseStudyScrollParallaxY) / 1;

		this._xParallax.tick();
		this._yParallax.tick();
		this._setPosition(1, true);
	}

	public dampenParallax(): void {
		this._easeInParallaxEffect();
		this._moveMuliplier = 0;
		let ob = { value: 0 };
		gsap.TweenMax.to(ob, 0.5, {
			ease: Config.linearEase,
			value: 1,
			onUpdate: () => {
				this._moveMuliplier = ob.value;
			}
		});
	}

	//Private
	//--------------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------------

	//Add the masked image to the container:
	private _createParticle(depth: number = -1): void {
		if (!this._$container) {
			console.warn('Could not create PIXI Particle because this._$container is not set.');
			return;
		}

		//No cacheAsBitmap (v5 relic): inert on Pixi 8.0-8.5; the 8.6+ shim
		//re-activates it and snapshots hidden tiles black (see WorkTileImagePixi).
		this._$image = new PIXI.Sprite(this._config.texture);
		this._$image.name = this._config.name;
		this._$image.anchor.x = 0.5;
		this._$image.anchor.y = 0.5;

		this._$container.addChild(this._$image);

		this._$image.x = this._config.x.value;
		this._$image.y = this._config.y.value;

		if (depth !== -2) {
			this._$backoutImage = new PIXI.Sprite(this._config.texture);
			this._$backoutImage.name = this._config.name + ' - blackout';
			this._$backoutImage.anchor.x = 0.5;
			this._$backoutImage.anchor.y = 0.5;
			this._$backoutImage.tint = 0x000000;
			this._$container.addChild(this._$backoutImage);
		}
	}

	private _setParallax(parallaxRatio: number = 1.0) {

		if ((!this._xParallax.active && !this._yParallax.active) || !this._parallaxActiveMultipler) {
			return;
		}

		let power = (device.innerWidth / 1920) * this._parallaxPower * this._parallaxActiveMultipler;
		let powerX = power;

		if (!globals.lastCordsFromTouch) {

			if (this._onCaseStudy) {
				powerX *= 7;
			}

			this._xParallax.target = powerX / 2 - (this._mouse.getMouseCords().x / device.innerWidth) * powerX;
			this._yParallax.target =
				(power * 3) / 2 - (this._mouse.getMouseCords().y / device.innerHeight) * (power * 3);

			return;
		} else {
			this._yParallax.ease = 1;
		}
	}

	private _setPosition(parallaxRatio: number = 1.0, caseStudy: boolean = false) {
		this._setParallax();

		if (caseStudy) {

			this._$image.position.set(
				this._$image.x + (this._xCaseStudy - this._$image.x) + this._xParallax.value,
				this._$image.y + (this._yCaseStudy + this._caseStudyScrollParallaxY - this._$image.y) + this._yParallax.value
			);
			return;
		}

		this._$image.position.set(
			this._$image.x + (this._x - this._$image.x) / this._parallaxEasePower + this._xParallax.value,
			this._y + this._yParallax.value
		);
	}
}
